import { useCallback, useEffect, useMemo, useRef } from "react";

import type { Node, Edge } from "@xyflow/react";

import applicationApi from "@domains/application/module/api/applicationApi";
import type { UseBuildLogsResult } from "src/domains/application/hooks/useBuildLogs";
import type { PipelineNodeData } from "src/domains/application/hooks/usePipelineGraph";

import type { PipelineFilter } from "../PipelineFilterBar";
import type { StageKey, JobInfo } from "../StagesSidebar";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UsePipelineActionsParams {
  alias: string;
  serviceNames: string[];
  nodes: Node[];
  edges: Edge[];
  activeFilter: PipelineFilter;
  // Refs that survive WS-driven re-renders
  selectedNodeIdRef: React.MutableRefObject<string | null>;
  selectedNodeDataRef: React.MutableRefObject<PipelineNodeData | null>;
  // Current selection state (for auto-select effects)
  selectedRunId: string | null;
  selectedStage: StageKey;
  selectedNodeId: string | null;
  selectedNodeData: PipelineNodeData | null;
  selectedPanelService: string | null;
  latestSagaId: string | null;
  effectiveServiceName: string | undefined;
  serviceLogs: UseBuildLogsResult;
  // State setters
  setSelectedNodeId: (id: string | null) => void;
  setSelectedNodeData: (data: PipelineNodeData | null) => void;
  setSelectedGroupService: (svc: string | null) => void;
  setSelectedPanelService: (svc: string | null) => void;
  setSelectedStage: (stage: StageKey) => void;
  setSelectedRunId: (runId: string | null) => void;
  setSelectedEnvSlug: (slug: string | null) => void;
  setPhaseJobList: (jobs: JobInfo[]) => void;
  // Retry/force-fail/redeploy loading + error setters
  setRetryLoading: (v: boolean) => void;
  setRetryError: (v: string | null) => void;
  setForceFailLoading: (v: boolean) => void;
  setForceFailError: (v: string | null) => void;
  setRedeployLoading: (v: boolean) => void;
  setRedeployError: (v: string | null) => void;
  // Refetch callbacks
  refetch: () => void;
  refetchApiRuns: () => void;
}

export interface UsePipelineActionsResult {
  handleNodeClick: (nodeId: string, data: PipelineNodeData) => void;
  handleCloseDetail: () => void;
  handleJobsUpdate: (
    jobs: Array<{
      name: string;
      status: string;
      message: string;
      startedAt: string | null;
      completedAt: string | null;
    }>,
  ) => void;
  handleBackToStages: () => void;
  handleRetryBuild: () => Promise<void>;
  handleForceFail: () => Promise<void>;
  handleRedeploy: () => Promise<void>;
  handleStageSelect: (stage: StageKey) => void;
  handleBackToRuns: () => void;
  handleEnvSwitch: (slug: string) => void;
  filteredNodeIds: Set<string>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * usePipelineActions — callbacks for user interactions in the pipeline tab.
 *
 * Extracted from PipelineTabInner. Handlers are declared in the same order
 * as the original component to satisfy React's rules of hooks.
 */
export function usePipelineActions(
  params: UsePipelineActionsParams,
): UsePipelineActionsResult {
  const {
    alias,
    nodes,
    activeFilter,
    selectedNodeIdRef,
    selectedNodeDataRef,
    selectedRunId,
    selectedStage,
    selectedNodeId,
    selectedNodeData,
    selectedPanelService,
    latestSagaId,
    effectiveServiceName,
    serviceLogs,
    setSelectedNodeId,
    setSelectedNodeData,
    setSelectedGroupService,
    setSelectedPanelService,
    setSelectedStage,
    setSelectedRunId,
    setSelectedEnvSlug,
    setPhaseJobList,
    setRetryLoading,
    setRetryError,
    setForceFailLoading,
    setForceFailError,
    setRedeployLoading,
    setRedeployError,
    refetch,
    refetchApiRuns,
  } = params;

  // Node click handler — syncs refs to survive WS-driven re-renders
  const handleNodeClick = useCallback(
    (nodeId: string, data: PipelineNodeData) => {
      selectedNodeIdRef.current = nodeId;
      selectedNodeDataRef.current = data;
      setSelectedNodeId(nodeId);
      setSelectedNodeData(data);
      // Always reset the two-level navigation on a new node click
      setSelectedPanelService(null);
      setSelectedGroupService(null);
      // Map node type to stage
      if (data.nodeType === "vcs-badge") setSelectedStage("setup");
      else if (data.nodeType === "group-build") setSelectedStage("build");
      else if (data.nodeType === "group-deploy") setSelectedStage("deploy");
      else if (data.nodeType === "group-ci") setSelectedStage("build");
    },
    [
      selectedNodeIdRef,
      selectedNodeDataRef,
      setSelectedNodeId,
      setSelectedNodeData,
      setSelectedPanelService,
      setSelectedGroupService,
      setSelectedStage,
    ],
  );

  const handleCloseDetail = useCallback(() => {
    selectedNodeIdRef.current = null;
    selectedNodeDataRef.current = null;
    setSelectedNodeId(null);
    setSelectedNodeData(null);
    setSelectedGroupService(null);
    setSelectedPanelService(null);
  }, [
    selectedNodeIdRef,
    selectedNodeDataRef,
    setSelectedNodeId,
    setSelectedNodeData,
    setSelectedGroupService,
    setSelectedPanelService,
  ]);

  // Callback for PhaseDetailView to export its jobs list
  const handleJobsUpdate = useCallback(
    (
      jobs: Array<{
        name: string;
        status: string;
        message: string;
        startedAt: string | null;
        completedAt: string | null;
      }>,
    ) => {
      setPhaseJobList(
        jobs.map((j) => ({
          name: j.name,
          status: j.status as JobInfo["status"],
        })),
      );
    },
    [setPhaseJobList],
  );

  // Back from jobs mode to stages mode
  const handleBackToStages = useCallback(() => {
    setSelectedStage("summary");
    setPhaseJobList([]);
    handleCloseDetail();
  }, [setSelectedStage, setPhaseJobList, handleCloseDetail]);

  // Retry build
  const handleRetryBuild = useCallback(async () => {
    setRetryLoading(true);
    setRetryError(null);
    try {
      const result = await applicationApi.retryBuild(alias);
      // Immediately select the new run so the DAG shows pending/building
      if (result.jobId) {
        setSelectedRunId(result.jobId);
        setSelectedNodeId(null);
        setSelectedNodeData(null);
      }
      // Refetch API runs to populate the rail with the new queued run
      setTimeout(() => {
        void refetchApiRuns();
        void refetch();
      }, 1500);
    } catch (err) {
      setRetryError(
        err instanceof Error ? err.message : "Failed to retry build",
      );
    } finally {
      setRetryLoading(false);
    }
  }, [
    alias,
    refetch,
    refetchApiRuns,
    setRetryLoading,
    setRetryError,
    setSelectedRunId,
    setSelectedNodeId,
    setSelectedNodeData,
  ]);

  // Force fail
  const handleForceFail = useCallback(async () => {
    setForceFailLoading(true);
    setForceFailError(null);
    try {
      await applicationApi.forceFailBuild(alias);
      setTimeout(() => void refetch(), 1500);
    } catch (err) {
      setForceFailError(
        err instanceof Error ? err.message : "Failed to force-fail build",
      );
    } finally {
      setForceFailLoading(false);
    }
  }, [alias, refetch, setForceFailLoading, setForceFailError]);

  // Redeploy (re-build + re-deploy the application)
  const handleRedeploy = useCallback(async () => {
    setRedeployLoading(true);
    setRedeployError(null);
    try {
      const result = await applicationApi.buildAndDeploy(alias);
      // Immediately select the new run so the DAG shows pending/building
      // state right away instead of waiting for the API runs refetch.
      if (result.jobId) {
        setSelectedRunId(result.jobId);
        setSelectedNodeId(null);
        setSelectedNodeData(null);
      }
      // Refetch API runs to populate the rail with the new queued run
      setTimeout(() => {
        void refetchApiRuns();
        void refetch();
      }, 1500);
    } catch (err) {
      setRedeployError(
        err instanceof Error ? err.message : "Failed to build & deploy",
      );
    } finally {
      setRedeployLoading(false);
    }
  }, [
    alias,
    refetch,
    refetchApiRuns,
    setRedeployLoading,
    setRedeployError,
    setSelectedRunId,
    setSelectedNodeId,
    setSelectedNodeData,
  ]);

  // Handle stage selection — maps to the existing node click logic
  const handleStageSelect = useCallback(
    (stage: StageKey) => {
      setSelectedStage(stage);
      if (stage === "summary") {
        handleCloseDetail();
      } else if (stage === "setup") {
        const vcsNode = nodes.find((n) => n.data?.nodeType === "vcs-badge");
        if (vcsNode) {
          handleNodeClick(vcsNode.id, vcsNode.data as PipelineNodeData);
        } else {
          handleCloseDetail();
        }
      } else if (stage === "build") {
        const buildNode = nodes.find((n) => n.data?.nodeType === "group-build");
        if (buildNode) {
          handleNodeClick(buildNode.id, buildNode.data as PipelineNodeData);
        }
      } else if (stage === "deploy") {
        const deployNode = nodes.find(
          (n) => n.data?.nodeType === "group-deploy",
        );
        if (deployNode) {
          handleNodeClick(deployNode.id, deployNode.data as PipelineNodeData);
        }
      }
    },
    [nodes, handleNodeClick, handleCloseDetail, setSelectedStage],
  );

  // Handle back-to-runs: deselect run and show Level 1
  // setRun(null) clears stage/job params automatically
  const handleBackToRuns = useCallback(() => {
    setSelectedRunId(null);
    setPhaseJobList([]);
    handleCloseDetail();
  }, [setSelectedRunId, setPhaseJobList, handleCloseDetail]);

  // Environment switch
  const handleEnvSwitch = useCallback(
    (slug: string) => {
      setSelectedEnvSlug(slug);
    },
    [setSelectedEnvSlug],
  );

  // When a run is selected, clear internal detail panel state.
  // The stage/job URL params are already cleared by the setRun hook.
  useEffect(() => {
    if (selectedRunId) {
      handleCloseDetail();
    }
  }, [selectedRunId, handleCloseDetail]);

  // When a stage is set from the URL (page reload) but no DAG node is
  // selected, auto-select the corresponding node so detail panels have data.
  useEffect(() => {
    if (!selectedRunId || selectedNodeId !== null) return;
    if (selectedStage === "setup") {
      const vcsNode = nodes.find((n) => n.data?.nodeType === "vcs-badge");
      if (vcsNode)
        handleNodeClick(vcsNode.id, vcsNode.data as PipelineNodeData);
    } else if (selectedStage === "build") {
      const buildNode = nodes.find((n) => n.data?.nodeType === "group-build");
      if (buildNode)
        handleNodeClick(buildNode.id, buildNode.data as PipelineNodeData);
    } else if (selectedStage === "deploy") {
      const deployNode = nodes.find((n) => n.data?.nodeType === "group-deploy");
      if (deployNode)
        handleNodeClick(deployNode.id, deployNode.data as PipelineNodeData);
    }
  }, [selectedRunId, selectedStage, selectedNodeId, nodes, handleNodeClick]);

  // Fetch build logs when a build service is selected (deduplicated by key)
  // Include selectedPanelService in the key so drilling into a group service always triggers a fetch
  const fetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    const targetSaga = selectedRunId ?? latestSagaId ?? "";
    if (selectedNodeData?.step === "build" && effectiveServiceName) {
      const key = `${targetSaga}:${effectiveServiceName}:${selectedNodeData.step}:${selectedPanelService ?? ""}`;
      if (fetchedForRef.current !== key) {
        fetchedForRef.current = key;
        void serviceLogs.refetch();
      }
    } else {
      fetchedForRef.current = null;
    }
  }, [
    selectedNodeData,
    effectiveServiceName,
    selectedRunId,
    latestSagaId,
    selectedPanelService,
    serviceLogs.refetch,
  ]);

  // Compute which nodes are visible given the active filter
  const filteredNodeIds = useMemo(() => {
    if (activeFilter === "all") return new Set<string>();
    const statusMap: Record<PipelineFilter, string[]> = {
      all: [],
      running: ["in_progress"],
      failed: ["failed"],
      completed: ["completed"],
    };
    const matchStatuses = statusMap[activeFilter] ?? [];
    const visible = new Set<string>();
    for (const node of nodes) {
      const nodeStatus = (node.data as PipelineNodeData)?.status;
      if (nodeStatus && matchStatuses.includes(nodeStatus)) {
        visible.add(node.id);
      }
    }
    return visible;
  }, [nodes, activeFilter]);

  return {
    handleNodeClick,
    handleCloseDetail,
    handleJobsUpdate,
    handleBackToStages,
    handleRetryBuild,
    handleForceFail,
    handleRedeploy,
    handleStageSelect,
    handleBackToRuns,
    handleEnvSwitch,
    filteredNodeIds,
  };
}
